import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { MOBILE } from '../../../../../utils/constants';
import type { Response } from '../CommentsSection';
import CommentInput from './ReplyInput';
import { UpVote } from './UpVote';
import { AcceptAnswer } from './AcceptAnswer';
import { Author } from './Author';

interface Props {
  response: Response;
  replyInputDepth?: number;
  currentDepth?: number;
  postId: string;
}

function CommentThread({ response, replyInputDepth = 1, currentDepth = 1, postId }: Props) {
  return (
    <div css={threadContainerStyles}>
      <UpVote currentDepth={currentDepth} voteCount={0} />
      <div css={contentContainerStyles}>
        <AcceptAnswer currentDepth={currentDepth} />
        <Author name={response.author?.name} badge={response.author?.badge} timeAgo={response.author?.timeAgo} />
        <div css={contentStyles}>{response.content}</div>
        {/* Render replies recursively */}
        {response.responses?.length > 0 && (
          <div css={repliesContainerStyles}>
            {response.responses.map((reply) => (
              <CommentThread
                key={reply.id}
                response={reply}
                replyInputDepth={replyInputDepth}
                currentDepth={currentDepth + 1}
                postId={postId}
              />
            ))}
          </div>
        )}
        {currentDepth <= replyInputDepth && <CommentInput postId={postId} parentId={response.id} />}
      </div>
    </div>
  );
}

const threadContainerStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  marginTop: '24px',
  width: '100%',
  boxSizing: 'border-box',
  [MOBILE.MEDIA_QUERY]: {
    marginTop: '16px',
  },
});

const contentContainerStyles = css({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
});

const contentStyles = css({
  marginBottom: '8px',
  color: token('color.text'),
  fontFamily: 'Inter, sans-serif',
  lineHeight: '1.5',
  [MOBILE.MEDIA_QUERY]: {
    marginBottom: '6px',
    fontSize: '14px',
  },
});

const repliesContainerStyles = css({
  marginTop: '16px',
  paddingLeft: '48px',
  position: 'relative',
  width: '100%',
  boxSizing: 'border-box',
  '::before': {
    content: "''",
    position: 'absolute',
    left: 0,
    top: 0,
    width: '2px',
    height: '100%',
    background: token('color.border'),
    borderRadius: '1px',
  },
  [MOBILE.MEDIA_QUERY]: {
    marginTop: '12px',
    paddingLeft: '32px',
  },
});

export default CommentThread;
